import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
// Salary data. Reads via PostgREST (owner-only RLS via am_i_owner(),
// migration 154); writes go through the authWrite Lambda which gates
// staff_cost on is_owner. The tab itself is owner-gated in Finance.jsx.
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';
import '../po/drawer.css'; // .form-input / .form-select / .form-label

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';

const moneyAUD0 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const moneyAUD2 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt0 = (n) => moneyAUD0.format(Number(n) || 0);
const fmt2 = (n) => moneyAUD2.format(Number(n) || 0);

// Tenure ("life with brand"): weeks for recent hires (< 8 weeks), else "Ny Mm".
function tenure(startStr) {
  if (!startStr) return '—';
  const start = new Date(startStr + 'T00:00:00');
  const now = new Date();
  if (isNaN(start.getTime()) || start > now) return '—';
  const days = Math.floor((now - start) / 86400000);
  if (days < 56) return `${Math.max(1, Math.round(days / 7))}w`;
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m}m`;
  return m === 0 ? `${y}y` : `${y}y ${m}m`;
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Gross → {tax, net} given the per-person tax split. tax_mode 'percent' (of
// gross) or 'amount' (fixed $). Always clamped so 0 ≤ tax ≤ gross.
function taxBreakdown(gross, mode, value) {
  const g = Number(gross) || 0;
  const v = Number(value) || 0;
  let tax = mode === 'amount' ? Math.min(Math.max(v, 0), g) : g * (Math.min(Math.max(v, 0), 100) / 100);
  tax = Math.max(0, Math.min(tax, g));
  return { tax, net: g - tax };
}

// Total costed to date — ESTIMATE: current monthly × months since start.
// Ignores past raises / variable invoicing; labelled "~est" in the UI.
function totalToDate(startStr, gross) {
  if (!startStr) return 0;
  const s = new Date(startStr + 'T00:00:00');
  const now = new Date();
  if (isNaN(s.getTime()) || s > now) return 0;
  const months = (now - s) / (86400000 * 30.4375);
  return (Number(gross) || 0) * months;
}

const COLS = ['Name', 'Cost/mo', 'Super', 'Tax', 'Net (pockets)', 'Start', 'Life', 'Fixed/Var', '$/day'];
const RIGHT = new Set(['Cost/mo', 'Super', 'Tax', 'Net (pockets)', '$/day']);
const TYPES = [{ key: 'staff', label: 'Staff' }, { key: 'consultant', label: 'Consultants' }];

function blankDraft(worker_type) {
  return { name: '', cost_monthly: '', super_monthly: '0', start_date: '', status: 'active', cost_type: 'fixed', worker_type, tax_mode: 'percent', tax_value: '0', notes: '' };
}

export default function StaffCost({ session }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);   // row id, '_new', or null
  const [draft, setDraft] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await aws.from('staff_cost').select('*').order('cost_monthly', { ascending: false });
    setRows(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => rows.filter(r => r.status === 'active'), [rows]);
  const dismissed = useMemo(() => rows.filter(r => r.status === 'dismissed'), [rows]);
  // Run-rate = TRUE cost to business = gross + super.
  const monthly = useMemo(() => active.reduce((s, r) => s + (Number(r.cost_monthly) || 0) + (Number(r.super_monthly) || 0), 0), [active]);
  const netTotal = useMemo(() => active.reduce((s, r) => s + taxBreakdown(r.cost_monthly, r.tax_mode, r.tax_value).net, 0), [active]);
  const weekly = monthly * 12 / 52;
  const annual = monthly * 12;

  function openEdit(r) {
    setDraft({ name: r.name || '', cost_monthly: r.cost_monthly ?? '', super_monthly: r.super_monthly ?? '0', start_date: r.start_date || '', status: r.status, cost_type: r.cost_type, worker_type: r.worker_type || 'staff', tax_mode: r.tax_mode || 'percent', tax_value: r.tax_value ?? '0', notes: r.notes || '' });
    setDeleteConfirm(false);
    setOpenId(r.id);
  }
  function openAdd(worker_type) { setDraft(blankDraft(worker_type)); setDeleteConfirm(false); setOpenId('_new'); }
  function closePane() { setOpenId(null); setDraft(null); setDeleteConfirm(false); }

  // Switching tax mode converts the value so the effective tax (and net)
  // stays the same — no silent reinterpretation (e.g. 30% of 1000 → $300).
  function setTaxMode(newMode) {
    setDraft(d => {
      if (!d || d.tax_mode === newMode) return d;
      const gross = Number(d.cost_monthly) || 0;
      const v = Number(d.tax_value) || 0;
      let nv;
      if (newMode === 'amount') nv = Math.round(gross * (Math.min(Math.max(v, 0), 100) / 100));
      else nv = gross > 0 ? Math.round((Math.min(Math.max(v, 0), gross) / gross) * 1000) / 10 : 0;
      return { ...d, tax_mode: newMode, tax_value: String(nv) };
    });
  }

  // Clamped tax for the active mode (used for live preview + save).
  const eff = useMemo(() => {
    if (!draft) return null;
    const gross = Number(draft.cost_monthly) || 0;
    const raw = Number(draft.tax_value) || 0;
    const value = draft.tax_mode === 'percent' ? Math.min(Math.max(raw, 0), 100) : Math.min(Math.max(raw, 0), gross);
    const { tax, net } = taxBreakdown(gross, draft.tax_mode, value);
    return { gross, value, tax, net };
  }, [draft]);

  function validDraft() {
    if (!draft) return false;
    const cost = parseFloat(draft.cost_monthly);
    return !!draft.name.trim() && !!draft.start_date && Number.isFinite(cost) && cost >= 0;
  }

  async function save() {
    if (!validDraft()) { toast.error('Missing details', 'Name, a valid monthly cost (≥ 0), and a start date are all required.'); return; }
    const isNew = openId === '_new';
    const payload = {
      name: draft.name.trim(),
      cost_monthly: parseFloat(draft.cost_monthly),
      start_date: draft.start_date,
      status: draft.status,
      cost_type: draft.cost_type,
      worker_type: draft.worker_type,
      tax_mode: draft.tax_mode,
      tax_value: eff.value,            // clamped to the active mode's range
      super_monthly: parseFloat(draft.super_monthly) || 0,
      notes: draft.notes?.trim() || null,
    };
    setSaving(true);
    try {
      if (isNew) await authWrite(session, 'staff_cost', 'insert', { ...payload, created_by: session?.code || null });
      else await authWrite(session, 'staff_cost', 'update', payload, { column: 'id', value: openId });
    } catch (e) {
      const { title, body } = humanizeError(e, isNew ? 'Could not add' : 'Could not save');
      toast.error(title, body); setSaving(false); return;
    }
    setSaving(false); closePane(); await load();
    toast.success(isNew ? 'Added' : 'Saved');
  }

  async function doDelete() {
    setSaving(true);
    try { await authWrite(session, 'staff_cost', 'delete', null, { column: 'id', value: openId }); }
    catch (e) { const { title, body } = humanizeError(e, 'Could not remove'); toast.error(title, body); setSaving(false); return; }
    setSaving(false); closePane(); await load(); toast.success('Removed');
  }

  function row(r) {
    const { tax, net } = taxBreakdown(r.cost_monthly, r.tax_mode, r.tax_value);
    const sup = Number(r.super_monthly) || 0;
    const total = (Number(r.cost_monthly) || 0) + sup;
    return (
      <tr key={r.id} onClick={() => openEdit(r)} style={{ cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <td style={td}>
          <div style={{ fontWeight: 500, color: '#0F1419' }}>{r.name}</div>
          {r.notes && <div style={{ fontSize: 10, color: '#8B9094', marginTop: 2, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.notes}</div>}
        </td>
        <td style={{ ...td, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#0F1419' }}>
          {fmt0(total)}{sup > 0 && <div style={{ fontSize: 9, fontWeight: 400, color: '#A1A1AA' }}>gross {fmt0(r.cost_monthly)}</div>}
        </td>
        <td style={{ ...td, textAlign: 'right', color: '#6E7278', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{sup > 0 ? fmt0(sup) : '—'}</td>
        <td style={{ ...td, textAlign: 'right', color: '#A56B00', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
          {fmt0(tax)}<div style={{ fontSize: 9, color: '#C5A86B' }}>{r.tax_mode === 'percent' ? `${Number(r.tax_value) || 0}%` : 'fixed'}</div>
        </td>
        <td style={{ ...td, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#0F8E6F' }}>{fmt0(net)}</td>
        <td style={{ ...td, color: '#5C6166', fontSize: 11 }}>{fmtDate(r.start_date)}</td>
        <td style={{ ...td, color: '#8B9094', fontSize: 11 }}>{tenure(r.start_date)}</td>
        <td style={td}>{costTypeChip(r.cost_type)}</td>
        <td style={{ ...td, textAlign: 'right', color: '#5C6166', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{fmt2(total / 30)}</td>
      </tr>
    );
  }

  function table(list, emptyMsg) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 820 }}>
          <thead><tr>{COLS.map((h, i) => (
            <th key={i} style={{ textAlign: RIGHT.has(h) ? 'right' : 'left', fontSize: 10, fontWeight: 500, color: '#a1a1aa', padding: '10px 14px', borderBottom: '1px solid #f4f4f5', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
          ))}</tr></thead>
          <tbody>{list.length === 0
            ? <tr><td colSpan={COLS.length} style={{ padding: 20, textAlign: 'center', color: '#a1a1aa' }}>{emptyMsg}</td></tr>
            : list.map(row)}</tbody>
        </table>
      </div>
    );
  }

  function section({ key, label }) {
    const act = rows.filter(r => r.worker_type === key && r.status === 'active');
    const sub = act.reduce((s, r) => s + (Number(r.cost_monthly) || 0) + (Number(r.super_monthly) || 0), 0);
    return (
      <div key={key} style={{ marginBottom: 26 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 400, color: '#0F1419', margin: 0, letterSpacing: '-0.01em' }}>{label}</h2>
            <div style={{ fontSize: 12, color: '#5C6166', marginTop: 3 }}>{act.length} active · <strong style={{ color: '#0F1419', fontVariantNumeric: 'tabular-nums' }}>{fmt0(sub)}/mo</strong></div>
          </div>
          <button onClick={() => openAdd(key)} style={addBtn}>+ Add {label.replace(/s$/, '').toLowerCase()}</button>
        </div>
        {table(act, `No active ${label.toLowerCase()}.`)}
      </div>
    );
  }

  function handleDownload() {
    const columns = [
      { label: 'Name', width: 24, value: r => r.name || '' },
      { label: 'Type', width: 14, value: r => r.worker_type === 'consultant' ? 'Consultant' : 'Staff' },
      { label: 'Cost/mo (AUD)', width: 14, numFmt: '"A$"#,##0', value: r => Number(r.cost_monthly || 0) },
      { label: 'Super/mo (AUD)', width: 14, numFmt: '"A$"#,##0', value: r => Number(r.super_monthly || 0) },
      { label: 'Tax/mo (AUD)', width: 14, numFmt: '"A$"#,##0', value: r => Math.round(taxBreakdown(r.cost_monthly, r.tax_mode, r.tax_value).tax) },
      { label: 'Net/mo (AUD)', width: 14, numFmt: '"A$"#,##0', value: r => Math.round(taxBreakdown(r.cost_monthly, r.tax_mode, r.tax_value).net) },
      { label: 'Total cost/mo (AUD)', width: 16, numFmt: '"A$"#,##0', value: r => Number(r.cost_monthly || 0) + Number(r.super_monthly || 0) },
      { label: 'Start Date', width: 14, value: r => r.start_date || '' },
      { label: 'Tenure', width: 12, value: r => tenure(r.start_date) },
      { label: 'Cost Type', width: 12, value: r => r.cost_type === 'variable' ? 'Variable' : 'Fixed' },
      { label: '$/day', width: 12, numFmt: '"A$"#,##0.00', value: r => (Number(r.cost_monthly || 0) + Number(r.super_monthly || 0)) / 30 },
      { label: 'Status', width: 12, value: r => r.status === 'dismissed' ? 'Dismissed' : 'Active' },
      { label: 'Notes', width: 30, value: r => r.notes || '' },
    ];
    const filename = `FON_Staff_Cost_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Staff Cost', columns, rows);
    logExportActivity(session, 'Staff Cost', filename);
  }

  return (
    <div>
      {/* ── Live run-rate headline (gross = true cost to the business) ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <Stat label="Weekly run-rate" value={fmt0(weekly)} sub="Total cost, incl super" />
        <Stat label="Monthly run-rate" value={fmt0(monthly)} sub={`${active.length} active · incl super`} />
        <Stat label="Annualised" value={fmt0(annual)} sub="12× monthly" />
        <Stat label="Net / mo (pocket)" value={fmt0(netTotal)} sub="After tax, all active" accent="#0F8E6F" />
      </div>

      {loading ? <div style={{ padding: 24, color: '#a1a1aa', fontSize: 13 }}>Loading…</div> : (
        <>
          {/* Active: Staff, then Consultants directly beneath */}
          {TYPES.map(section)}

          {/* Dismissed — all types, collapsed, at the very bottom */}
          {dismissed.length > 0 && (
            <div style={{ marginTop: 10, borderTop: '1px solid #EFEDE7', paddingTop: 16 }}>
              <button onClick={() => setShowDismissed(o => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: SERIF, fontSize: 17, fontWeight: 400, color: '#5C6166', padding: '2px 0', letterSpacing: '-0.01em' }}>
                <span style={{ fontSize: 12, color: '#8B9094', width: 12 }}>{showDismissed ? '▾' : '▸'}</span>
                Dismissed <span style={{ fontSize: 14, color: '#8B9094' }}>({dismissed.length})</span>
              </button>
              {showDismissed && <div style={{ marginTop: 10 }}>{table(dismissed, '')}</div>}
            </div>
          )}
        </>
      )}

      {/* ── Side pane: edit / add / delete ── */}
      {openId !== null && draft && createPortal(
        <>
          <div onClick={() => !saving && closePane()} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(3px)' }} />
          <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 201, width: 460, maxWidth: '94vw', background: '#fff', boxShadow: '-12px 0 48px rgba(0,0,0,0.16)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e4e4e7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#0F1419', fontFamily: SERIF }}>{openId === '_new' ? 'Add person' : (draft.name || 'Edit')}</div>
              <button onClick={closePane} disabled={saving} style={{ border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, color: '#8B9094', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {/* Derived snapshot */}
              {openId !== '_new' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
                  <Mini label="Life with brand" value={tenure(draft.start_date)} />
                  <Mini label="Total costed to date" value={`${fmt0(totalToDate(draft.start_date, (Number(draft.cost_monthly) || 0) + (Number(draft.super_monthly) || 0)))}`} hint="~est, incl super" />
                </div>
              )}

              <Seg label="Type" value={draft.worker_type} onChange={v => setDraft(d => ({ ...d, worker_type: v }))} options={[['staff', 'Staff'], ['consultant', 'Consultant']]} />
              <Seg label="Status" value={draft.status} onChange={v => setDraft(d => ({ ...d, status: v }))} options={[['active', 'Active'], ['dismissed', 'Dismissed']]} />

              <div className="form-group"><label className="form-label">Name</label>
                <input className="form-input" value={draft.name} autoFocus onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} /></div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Gross / mo (AUD)</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={draft.cost_monthly} onChange={e => setDraft(d => ({ ...d, cost_monthly: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Super / mo (AUD)</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={draft.super_monthly} onChange={e => setDraft(d => ({ ...d, super_monthly: e.target.value }))} /></div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Start date</label>
                  <input className="form-input" type="date" value={draft.start_date} onChange={e => setDraft(d => ({ ...d, start_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Cost type</label>
                  <select className="form-select" value={draft.cost_type} onChange={e => setDraft(d => ({ ...d, cost_type: e.target.value }))}>
                    <option value="fixed">Fixed</option><option value="variable">Variable</option>
                  </select></div>
              </div>

              {/* Tax editor — % or $, live net, convert-on-toggle */}
              <div className="form-group">
                <label className="form-label">Tax</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', border: '1px solid var(--border-input, #d4d4d8)', borderRadius: 8, overflow: 'hidden' }}>
                    {[['percent', '%'], ['amount', '$']].map(([m, lbl]) => (
                      <button key={m} type="button" onClick={() => setTaxMode(m)}
                        style={{ padding: '0 14px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: draft.tax_mode === m ? '#7c3aed' : '#fff', color: draft.tax_mode === m ? '#fff' : '#52525b' }}>{lbl}</button>
                    ))}
                  </div>
                  <input className="form-input" type="number" step={draft.tax_mode === 'percent' ? '0.1' : '1'} min="0"
                    max={draft.tax_mode === 'percent' ? 100 : undefined}
                    value={draft.tax_value} onChange={e => setDraft(d => ({ ...d, tax_value: e.target.value }))}
                    style={{ flex: 1 }} placeholder={draft.tax_mode === 'percent' ? '% of gross' : '$ per month'} />
                </div>
                {eff && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#5C6166', display: 'flex', gap: 14, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
                    <span>Gross <strong style={{ color: '#0F1419' }}>{fmt0(eff.gross)}</strong></span>
                    <span>Super <strong style={{ color: '#6E7278' }}>{fmt0(Number(draft.super_monthly) || 0)}</strong></span>
                    <span>Tax <strong style={{ color: '#A56B00' }}>{fmt0(eff.tax)}</strong></span>
                    <span>Net <strong style={{ color: '#0F8E6F' }}>{fmt0(eff.net)}</strong></span>
                    <span>Total cost <strong style={{ color: '#0F1419' }}>{fmt0(eff.gross + (Number(draft.super_monthly) || 0))}</strong></span>
                  </div>
                )}
              </div>

              <div className="form-group"><label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} /></div>
            </div>

            <div style={{ padding: '12px 24px', borderTop: '1px solid #e4e4e7', display: 'flex', gap: 8, alignItems: 'center' }}>
              {openId !== '_new' && !deleteConfirm && (
                <button className="btn btn-sm" disabled={saving} onClick={() => setDeleteConfirm(true)} style={{ color: '#dc2626', border: '1px solid #f1d4d4', background: '#fff' }}>Delete</button>
              )}
              {openId !== '_new' && deleteConfirm && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginRight: 'auto' }}>
                  <span style={{ fontSize: 12, color: '#dc2626' }}>Delete {draft.name}?</span>
                  <button className="btn btn-danger btn-sm" disabled={saving} onClick={doDelete}>{saving ? '…' : 'Yes, remove'}</button>
                  <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => setDeleteConfirm(false)}>No</button>
                </div>
              )}
              {!deleteConfirm && <div style={{ flex: 1 }} />}
              {!deleteConfirm && <button className="btn btn-secondary btn-sm" disabled={saving} onClick={closePane}>Cancel</button>}
              {!deleteConfirm && <button className="btn btn-primary btn-sm" disabled={saving || !validDraft()} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>}
            </div>
          </aside>
        </>, document.body)}
    </div>
  );
}

// ── small presentational helpers ──
const td = { padding: '10px 14px', borderBottom: '1px solid #fafafa' };
const addBtn = { padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' };

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 10, fontWeight: 500, color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent || '#0F1419', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#8B9094', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Mini({ label, value, hint }) {
  return (
    <div style={{ background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 9.5, fontWeight: 500, color: '#8B9094', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#0F1419', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: '#A1A1AA', marginTop: 1 }}>{hint}</div>}
    </div>
  );
}

function Seg({ label, value, onChange, options }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', border: '1px solid var(--border-input, #d4d4d8)', borderRadius: 8, overflow: 'hidden', width: 'fit-content' }}>
        {options.map(([v, lbl]) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            style={{ padding: '7px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: value === v ? '#7c3aed' : '#fff', color: value === v ? '#fff' : '#52525b' }}>{lbl}</button>
        ))}
      </div>
    </div>
  );
}

function costTypeChip(type) {
  const variable = type === 'variable';
  return (
    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: variable ? '#fef3c7' : '#f4f4f5', color: variable ? '#b45309' : '#52525b' }}>
      {variable ? 'Variable' : 'Fixed'}
    </span>
  );
}
